// Page preloader — CSS draws the Klik mark in; this module runs the loading loop,
// the progress counter and the exit (lime burst + upward wipe), then frees the page.
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ACTIVE_CLASS = 'kd-preloading';
const MAX_WAIT = 6000;
const MARK_ORIGIN = '42.33 42.33';

let done = true;
const queue = [];

/** Run a callback once the preloader has gone (immediately if there is none). */
export function onPreloaderDone(cb) {
  if (done) cb();
  else queue.push(cb);
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pageLoaded = () =>
  document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));

// Resolves when the finite CSS intro keyframes are over (instantly if already played).
function introFinished(root) {
  if (typeof root.getAnimations !== 'function') return wait(1600);

  const intro = root
    .getAnimations({ subtree: true })
    .filter((a) => a.effect && a.effect.getComputedTiming().iterations !== Infinity);

  return Promise.all(intro.map((a) => a.finished.catch(() => {})));
}

export default function initPreloader() {
  const root = document.getElementById('kd-preloader');
  const html = document.documentElement;
  if (!root) return;

  if (!html.classList.contains(ACTIVE_CLASS)) {
    root.remove();
    return;
  }

  done = false;
  window.__kdPreloaderReady = true;
  if (window.__kdLenis) window.__kdLenis.stop();

  const q = (sel) => root.querySelector(sel);
  const mark = q('.kd-preloader__mark');
  const sqTr = q('.kd-preloader__sq--tr');
  const sqBl = q('.kd-preloader__sq--bl');
  const star = q('.kd-preloader__star');
  const countEl = q('[data-kd-count]');
  const barFill = q('.kd-preloader__bar-fill');

  const progress = { v: 0 };
  const render = () => {
    countEl.textContent = Math.round(progress.v * 100);
    gsap.set(barFill, { scaleX: progress.v });
  };

  const crawl = gsap.to(progress, { v: 0.86, duration: 2.6, ease: 'power2.out', onUpdate: render });

  let exiting = false;
  let pulse = null;

  const spin = () => {
    if (exiting) return;
    gsap.to(mark, {
      rotation: '+=180',
      svgOrigin: MARK_ORIGIN,
      duration: 0.9,
      ease: 'power3.inOut',
      onComplete: () => gsap.delayedCall(0.2, spin),
    });
  };

  const intro = introFinished(root).then(() => {
    root.classList.add('is-live');
    if (exiting) return;
    spin();
    pulse = gsap.to(star, {
      scale: 1.2,
      transformOrigin: '50% 50%',
      duration: 0.55,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
    });
  });

  const finish = () => {
    done = true;
    root.remove();
    if (window.__kdLenis) window.__kdLenis.start();
    ScrollTrigger.refresh();
    queue.splice(0).forEach((cb) => cb());
    document.dispatchEvent(new CustomEvent('kd:preloader-done'));
  };

  const exit = () => {
    if (exiting) return;
    exiting = true;
    crawl.kill();
    root.classList.add('is-live');
    if (pulse) pulse.kill();
    gsap.killTweensOf(mark);

    const rotation = gsap.getProperty(mark, 'rotation') || 0;
    const tl = gsap.timeline({ onComplete: finish });

    tl.to(progress, { v: 1, duration: 0.5, ease: 'power2.inOut', onUpdate: render })
      .to(mark, { rotation: Math.ceil(rotation / 180) * 180, svgOrigin: MARK_ORIGIN, duration: 0.5, ease: 'power3.out' }, 0)
      .to(star, { scale: 1, transformOrigin: '50% 50%', duration: 0.3 }, 0)
      .addLabel('out', '+=0.1')
      .to(q('.kd-preloader__label').children, { yPercent: -110, duration: 0.5, stagger: 0.02, ease: 'power3.in' }, 'out')
      .to([q('.kd-preloader__sub'), q('.kd-preloader__count')], { yPercent: -110, duration: 0.55, ease: 'power3.in' }, 'out')
      .to(q('.kd-preloader__bar'), { scaleX: 0, transformOrigin: 'right center', duration: 0.6, ease: 'power3.inOut' }, 'out')
      .to(sqTr, { x: 16, y: -16, duration: 0.6, ease: 'power3.inOut' }, 'out')
      .to(sqBl, { x: -16, y: 16, duration: 0.6, ease: 'power3.inOut' }, 'out')
      .to(star, { scale: 1.8, rotation: 90, transformOrigin: '50% 50%', duration: 0.6, ease: 'power3.inOut' }, 'out')
      .fromTo(
        q('.kd-preloader__burst'),
        { clipPath: 'circle(0% at 50% 50%)' },
        { clipPath: 'circle(75% at 50% 50%)', duration: 0.9, ease: 'expo.inOut' },
        'out+=0.35'
      )
      .add(() => {
        root.classList.add('is-exiting');
        html.classList.remove(ACTIVE_CLASS);
      })
      .fromTo(
        root,
        { clipPath: 'inset(0% 0% 0% 0%)' },
        { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.95, ease: 'expo.inOut' }
      );
  };

  // ?kd-preloader=hold keeps the loop running for design tweaks.
  if (/[?&]kd-preloader=hold\b/.test(window.location.search)) return;

  Promise.race([Promise.all([pageLoaded(), intro]), wait(MAX_WAIT)]).then(exit);
}
